'use client';
import { AuthAffordance, AuthAffordanceVariant } from '@vs-shop/commerce-ui/authAffordance';
import { Alert } from '@vs-shop/ui/alert';
import { Button } from '@vs-shop/ui/button';
import { Input } from '@vs-shop/ui/input';
import { captureCause } from '../../shared/utils/sentry';
import { Exit } from 'effect';
import { useState, useTransition } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { loginForm } from '../../data/loginForm';

import effectTsResolver from '@vs-shop/engine/adapter/effect-hook-form/effectTsResolver';
import { loginCustomerAtomFn } from '../../effect/atom/auth/mutation/loginCustomer';
import type { LoginFormData } from '@vs-shop/schemas/authForms';
import { LoginFormSchema } from '@vs-shop/schemas/authForms';
import { useAtomSet } from '@effect/atom-react';

type Props = {
  onSuccessAction: () => void;
  onSwitchToRegisterAction: () => void;
  onSwitchToResetPasswordAction: () => void;
  displaySwitchToRegister?: boolean;
};

export default function LoginForm({
  onSuccessAction,
  onSwitchToRegisterAction,
  onSwitchToResetPasswordAction,
  displaySwitchToRegister = true,
}: Props) {
  const intl = useIntl();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [isPending, startTransition] = useTransition();
  const loginMutation = useAtomSet(loginCustomerAtomFn, { mode: 'promiseExit' });

  const {
    register,
    handleSubmit,
    formState: { errors, touchedFields },
  } = useForm<LoginFormData>({
    resolver: effectTsResolver(LoginFormSchema),
    mode: 'onBlur',
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = (data: LoginFormData) => {
    setErrorMessage(null);

    startTransition(async () => {
      const result = await loginMutation({
        email: data.email,
        password: data.password,
      });

      Exit.match(result, {
        onFailure: (cause) => {
          captureCause(cause, {
            component: 'LoginForm',
            action: 'Login failed',
            additionalData: {
              email: data.email,
            },
          });

          setErrorMessage(
            intl.formatMessage({
              id: 'component.loginForm.apiError',
              defaultMessage: 'Neplatné přihlašovací údaje',
            })
          );
        },
        onSuccess: (_response) => {
          onSuccessAction();
        },
      });
    });
  };

  const minCharsStr = intl.formatNumber(loginForm.minimumCharacters, {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  return (
    <div>
      {errorMessage && (
        <Alert variant="destructive" data-slot="login-form-error" className="mb-3" role="alert">
          {errorMessage}
        </Alert>
      )}

      <form noValidate autoComplete="on" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="space-y-2">
          <Input
            {...register('email')}
            id="email"
            type="email"
            autoComplete="username"
            label={intl.formatMessage({
              id: 'component.loginForm.email.label',
              defaultMessage: 'E-mail',
            })}
            placeholder={intl.formatMessage({
              id: 'component.loginForm.email.placeholder',
              defaultMessage: 'E-mail',
            })}
            error={errors.email?.message ?? null}
            isTouched={!!touchedFields.email}
          />

          <Input
            {...register('password')}
            id="password"
            type="password"
            autoComplete="current-password"
            label={intl.formatMessage({
              id: 'component.loginForm.password.label',
              defaultMessage: 'Heslo',
            })}
            placeholder={intl.formatMessage(
              {
                id: 'component.loginForm.password.placeholder',
                defaultMessage: 'Heslo (min. {minimumCharacters} znaků)',
              },
              { minimumCharacters: minCharsStr }
            )}
            error={errors.password?.message ?? null}
            isTouched={!!touchedFields.password}
          />

          <div className="w-full text-end">
            {/* The offer paints itself: no arm of the affordance is this look —
                small, in the alert colour, and not underlined. */}
            <AuthAffordance
              variant={AuthAffordanceVariant.UNSTYLED}
              data-slot="login-forgot-password"
              onClick={onSwitchToResetPasswordAction}
              className="cursor-pointer text-sm text-destructive"
            >
              {intl.formatMessage({
                id: 'component.loginForm.forgotPassword',
                defaultMessage: 'Zapomenuté heslo',
              })}
            </AuthAffordance>
          </div>
        </div>

        <Button
          type="submit"
          variant="primary"
          className="w-full uppercase"
          loading={isPending}
          disabled={isPending}
        >
          {intl.formatMessage({
            id: 'component.loginForm.button.submit',
            defaultMessage: 'Přihlásit',
          })}
        </Button>
      </form>

      {displaySwitchToRegister && (
        <div className="mt-6 text-center">
          <AuthAffordance variant={AuthAffordanceVariant.PLAIN} onClick={onSwitchToRegisterAction}>
            {intl.formatMessage({
              id: 'component.loginForm.switchToRegister',
              defaultMessage: 'Registrovat',
            })}
          </AuthAffordance>
        </div>
      )}
    </div>
  );
}
